/**
 * The last line of defence against a blank page.
 *
 * A React component that throws during render takes its whole tree down with
 * it — since React 16 the default is to unmount everything rather than leave a
 * half-rendered interface. Without a boundary the user gets a white screen and
 * no explanation at all, which is the single worst failure mode a single-page
 * application has: it looks identical to a network outage, a crashed browser
 * tab, and a bug in the user's own machine.
 *
 * A class component, because error boundaries have no hook equivalent —
 * `componentDidCatch` and `getDerivedStateFromError` are the only API React
 * offers. This is not legacy code that nobody got round to converting.
 *
 * Scope it narrowly. One boundary around the whole application turns any bug
 * anywhere into a full-page error; one around each major region keeps the rest
 * of the interface usable, which is what the default copy here assumes.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the children. Receives a reset callback. */
  fallback: (error: Error, reset: () => void) => ReactNode;
  /**
   * Reported to whatever collects errors.
   *
   * Not optional in practice: a boundary that catches silently converts a
   * loud, findable crash into an invisible one, and the bug then survives
   * until a user thinks to mention it.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    // Logged even when a reporter is supplied. During development the console
    // is where a developer looks first, and a boundary that swallows the stack
    // makes the bug considerably harder to find than no boundary at all.
    console.error("A component tree failed to render.", error, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) return this.props.fallback(this.state.error, this.reset);
    return this.props.children;
  }
}
